import { useState, useEffect, useRef } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import FileUpload, { getFileUrl } from "@/components/FileUpload";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Music2, Play, Pause, Star, ChevronDown, ChevronUp } from "lucide-react";

const RELEASE_TYPES = [
  { value: "single", label: "Single" },
  { value: "ep", label: "EP" },
  { value: "album", label: "Album" },
];

const empty = {
  title: "",
  artist_name: "Luxifer 6",
  album_name: "",
  release_type: "single",
  cover_art: "",
  additional_artwork: [],
  description: "",
  release_date: "",
  release_year: "",
  audio_preview: "",
  preview_start_time: 0,
  download_file: "",
  wav_file: "",
  streaming_links: { spotify: "", youtube: "", apple_music: "" },
  price: 0,
  sale_price: 0,
  is_free: false,
  is_visible: true,
  featured: false,
  exclusive: false,
  lyrics: "",
  credits: "",
  producer: "",
  songwriters: "",
  genre: "",
  bpm: 0,
  isrc: "",
  explicit: false,
};

function MiniPlayer({ src }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setPlaying(!playing);
  };
  if (!src) return null;
  return (
    <div className="flex items-center gap-2">
      <audio ref={audioRef} src={getFileUrl(src)} onEnded={() => setPlaying(false)} />
      <button onClick={toggle} className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center hover:bg-[#D4AF37]/40 transition-colors">
        {playing ? <Pause className="w-3.5 h-3.5 text-[#D4AF37]" /> : <Play className="w-3.5 h-3.5 text-[#D4AF37] ml-0.5" />}
      </button>
      <span className="text-[10px] text-white/30">Preview</span>
    </div>
  );
}

const Field = ({ label, hint, children, className = "" }) => (
  <div className={className}>
    <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">
      {label} {hint && <span className="text-white/20 normal-case tracking-normal">{hint}</span>}
    </label>
    {children}
  </div>
);

const Text = (props) => (
  <input
    {...props}
    className={`w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] placeholder:text-white/20 ${props.className || ""}`}
  />
);

const Area = (props) => (
  <textarea
    {...props}
    className={`w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] placeholder:text-white/20 resize-none ${props.className || ""}`}
  />
);

const Select = ({ children, ...props }) => (
  <select
    {...props}
    className={`w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] ${props.className || ""}`}
  >
    {children}
  </select>
);

export default function MusicManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...empty });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showLinks, setShowLinks] = useState(false);

  const fetch_ = () => api.get("/music?admin=true").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { fetch_(); }, []);

  const openAdd = () => {
    setEditId(null);
    setForm({ ...empty });
    setShowAdvanced(false); setShowLinks(false);
    setOpen(true);
  };
  const openEdit = (item) => {
    setEditId(item.id);
    setForm({
      ...empty,
      ...item,
      streaming_links: { ...empty.streaming_links, ...(item.streaming_links || {}) },
      additional_artwork: item.additional_artwork || [],
    });
    setShowAdvanced(false); setShowLinks(false);
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    // Derive release_year from release_date if missing
    const payload = { ...form };
    if (!payload.release_year && payload.release_date) {
      payload.release_year = String(payload.release_date).slice(0, 4);
    }
    if (payload.is_free) payload.price = 0;
    try {
      if (editId) await api.put(`/music/${editId}`, payload);
      else await api.post("/music", payload);
      toast.success(editId ? "Track updated" : "Track added");
      setOpen(false);
      fetch_();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this track?")) return;
    try { await api.delete(`/music/${id}`); toast.success("Deleted"); fetch_(); }
    catch { toast.error("Delete failed"); }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLink = (k, v) => setForm((f) => ({ ...f, streaming_links: { ...f.streaming_links, [k]: v } }));

  const priceLabel = (item) => {
    if (item.is_free || item.price === 0) return "Free";
    if (item.sale_price > 0 && item.sale_price < item.price) return `$${item.sale_price.toFixed(2)}`;
    return `$${(item.price || 0).toFixed(2)}`;
  };

  return (
    <div data-testid="admin-music-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl">MUSIC</h1>
          <p className="text-white/40 text-sm">{items.length} tracks</p>
        </div>
        <Button data-testid="add-music-btn" onClick={openAdd} className="bg-[#D4AF37] text-black hover:bg-white text-sm"><Plus className="w-4 h-4 mr-1" /> Add Track</Button>
      </div>

      {loading ? <p className="text-white/30">Loading...</p> : items.length === 0 ? (
        <div className="text-center py-16 text-white/30">
          <Music2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>No music yet. Add your first track.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} data-testid={`music-item-${item.id}`} className={`bg-[#111111] border border-white/5 p-4 rounded flex items-center gap-4 hover:border-white/10 transition-colors ${item.is_visible === false ? "opacity-50" : ""}`}>
              {item.cover_art ? (
                <img src={getFileUrl(item.cover_art)} alt="" className="w-14 h-14 object-cover rounded flex-shrink-0" />
              ) : (
                <div className="w-14 h-14 bg-[#1A1A1A] rounded flex items-center justify-center flex-shrink-0"><Music2 className="w-5 h-5 text-white/20" /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold truncate">{item.title}</p>
                  <span className="text-[9px] uppercase text-white/40 border border-white/10 px-1.5 py-0.5 rounded">{item.release_type || "single"}</span>
                  {item.explicit && <span className="text-[9px] uppercase text-white/70 border border-white/20 px-1.5 py-0.5 rounded font-bold">Explicit</span>}
                  {item.exclusive && <span className="text-[9px] uppercase bg-[#8B0000] text-white px-1.5 py-0.5 rounded font-bold flex-shrink-0">Exclusive</span>}
                  {item.featured && <Star className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />}
                  {item.is_visible === false && <span className="text-[9px] uppercase text-white/40 border border-white/20 px-1.5 py-0.5 rounded">Hidden</span>}
                </div>
                <p className="text-xs text-white/40 truncate">
                  {item.artist_name || "Luxifer 6"}
                  {item.album_name ? ` \u00b7 ${item.album_name}` : ""}
                  {item.release_date ? ` \u00b7 ${item.release_date}` : ""}
                  {item.genre ? ` \u00b7 ${item.genre}` : ""}
                </p>
                <MiniPlayer src={item.audio_preview} />
              </div>
              <span className="text-sm text-[#D4AF37] font-semibold hidden sm:block">{priceLabel(item)}</span>
              <Button variant="ghost" size="icon" onClick={() => openEdit(item)} className="text-white/40 hover:text-white"><Pencil className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editId ? "EDIT TRACK" : "ADD TRACK"}</DialogTitle>
            <DialogDescription className="text-white/40 text-xs">
              Complete metadata is embedded into MP3 downloads (title, artist, album, year, cover art, producer, songwriters, lyrics).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            {/* CORE */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Title *"><Text data-testid="music-title-input" value={form.title} onChange={(e) => setField("title", e.target.value)} /></Field>
              <Field label="Artist Name"><Text data-testid="music-artist-input" value={form.artist_name} onChange={(e) => setField("artist_name", e.target.value)} placeholder="Luxifer 6" /></Field>
              <Field label="Album Name"><Text value={form.album_name} onChange={(e) => setField("album_name", e.target.value)} placeholder="(optional)" /></Field>
              <Field label="Release Type">
                <Select value={form.release_type} onChange={(e) => setField("release_type", e.target.value)}>
                  {RELEASE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </Select>
              </Field>
            </div>

            <Field label="Description"><Area rows={2} value={form.description} onChange={(e) => setField("description", e.target.value)} /></Field>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Release Date"><Text type="date" value={form.release_date} onChange={(e) => setField("release_date", e.target.value)} /></Field>
              <Field label="Release Year" hint="(auto-fills from date)"><Text value={form.release_year} onChange={(e) => setField("release_year", e.target.value)} placeholder="2026" /></Field>
              <Field label="Genre"><Text value={form.genre} onChange={(e) => setField("genre", e.target.value)} placeholder="Trap, Hip-Hop, Luxury Rap…" /></Field>
            </div>

            {/* ARTWORK + AUDIO */}
            <div className="border-t border-white/5 pt-4 space-y-3">
              <FileUpload label="Cover Art (embedded in downloaded MP3)" accept="image/*" value={form.cover_art} onChange={(v) => setField("cover_art", v)} />
              <FileUpload label="Audio Preview (30s snippet)" accept="audio/*" value={form.audio_preview} onChange={(v) => setField("audio_preview", v)} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FileUpload label="Full Track — MP3" accept="audio/mpeg,audio/mp3,.mp3" value={form.download_file} onChange={(v) => setField("download_file", v)} />
                <FileUpload label="Full Track — WAV (optional)" accept="audio/wav,.wav" value={form.wav_file} onChange={(v) => setField("wav_file", v)} />
              </div>
              <Field label="Preview Start Time" hint="(seconds — where the 30s preview begins)">
                <Text type="number" min="0" value={form.preview_start_time} onChange={(e) => setField("preview_start_time", parseInt(e.target.value) || 0)} />
              </Field>
            </div>

            {/* COMMERCE */}
            <div className="border-t border-white/5 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Price ($)"><Text type="number" step="0.01" min="0" value={form.price} onChange={(e) => setField("price", parseFloat(e.target.value) || 0)} disabled={form.is_free} /></Field>
                <Field label="Sale Price ($)" hint="(0 = no sale)"><Text type="number" step="0.01" min="0" value={form.sale_price} onChange={(e) => setField("sale_price", parseFloat(e.target.value) || 0)} disabled={form.is_free} /></Field>
                <div className="flex flex-col justify-end gap-2">
                  <div className="flex items-center gap-3">
                    <Switch checked={form.is_free} onCheckedChange={(v) => setField("is_free", v)} />
                    <label className="text-sm text-white/60">Free Download</label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={form.is_visible !== false} onCheckedChange={(v) => setField("is_visible", v)} />
                    <label className="text-sm text-white/60">Visible on Site</label>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-3 pt-4">
                <div className="flex items-center gap-3">
                  <Switch checked={form.featured} onCheckedChange={(v) => setField("featured", v)} />
                  <label className="text-sm text-white/60">Featured / New Release</label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={form.exclusive} onCheckedChange={(v) => setField("exclusive", v)} />
                  <label className="text-sm text-white/60">Website Exclusive</label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={form.explicit} onCheckedChange={(v) => setField("explicit", v)} />
                  <label className="text-sm text-white/60">Explicit Content</label>
                </div>
              </div>
            </div>

            {/* ADVANCED (collapsible) */}
            <div className="border-t border-white/5 pt-3">
              <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-2 text-xs text-white/40 hover:text-white/70 transition-colors">
                {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                Advanced metadata — credits, lyrics, ISRC, BPM
              </button>
              {showAdvanced && (
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Producer"><Text value={form.producer} onChange={(e) => setField("producer", e.target.value)} /></Field>
                    <Field label="Songwriters"><Text value={form.songwriters} onChange={(e) => setField("songwriters", e.target.value)} /></Field>
                    <Field label="BPM"><Text type="number" min="0" value={form.bpm} onChange={(e) => setField("bpm", parseInt(e.target.value) || 0)} /></Field>
                    <Field label="ISRC" hint="(e.g. USRC17607839)"><Text value={form.isrc} onChange={(e) => setField("isrc", e.target.value)} /></Field>
                  </div>
                  <Field label="Credits"><Area rows={2} value={form.credits} onChange={(e) => setField("credits", e.target.value)} placeholder="Mixed by … · Mastered by … · Cover by …" /></Field>
                  <Field label="Lyrics" hint="(embedded into MP3)"><Area rows={5} value={form.lyrics} onChange={(e) => setField("lyrics", e.target.value)} /></Field>
                </div>
              )}
            </div>

            {/* STREAMING */}
            <div className="border-t border-white/5 pt-3">
              <button type="button" onClick={() => setShowLinks(!showLinks)} className="flex items-center gap-2 text-xs text-white/40 hover:text-white/70 transition-colors">
                {showLinks ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                Streaming links (Spotify / Apple / YouTube)
              </button>
              {showLinks && (
                <div className="space-y-3 mt-3">
                  {["spotify", "apple_music", "youtube"].map((k) => (
                    <Text key={k} value={form.streaming_links?.[k] || ""} onChange={(e) => setLink(k, e.target.value)}
                      placeholder={k.replace("_", " ").replace(/^\w/, (c) => c.toUpperCase()) + " URL (optional)"}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10 text-white/60">Cancel</Button>
              <Button data-testid="music-save-btn" onClick={handleSave} className="bg-[#D4AF37] text-black hover:bg-white">{editId ? "Update" : "Add"} Track</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
